import { z } from 'zod';
import { isoDateSchema, moneyMinorSchema } from '../api';
import { BATCH_STATUS, PURCHASE_RETURN_REASON, SUPPLIER_PAYMENT_MODE } from '../procurement';
const quantitySchema = z.number().min(0.001).max(100_000);
const noteSchema = z.string().trim().max(600);
export const supplierPaymentAllocationSchema = z.object({
    grnId: z.string().uuid(),
    amountMinor: moneyMinorSchema.min(1),
});
export const createSupplierPaymentSchema = z
    .object({
    supplierId: z.string().uuid(),
    mode: z.enum(SUPPLIER_PAYMENT_MODE),
    amountMinor: moneyMinorSchema.min(1),
    reference: z.string().trim().max(120).optional(),
    notes: noteSchema.optional(),
    allocations: z.array(supplierPaymentAllocationSchema).max(100).default([]),
    allocateOldestFirst: z.boolean().default(false),
})
    .superRefine((value, context) => {
    const allocated = value.allocations.reduce((sum, allocation) => sum + allocation.amountMinor, 0);
    if (allocated > value.amountMinor) {
        context.addIssue({ code: 'custom', path: ['allocations'], message: 'The allocations add up to more than the payment' });
    }
    if (value.allocations.length > 0 && value.allocateOldestFirst) {
        context.addIssue({ code: 'custom', path: ['allocateOldestFirst'], message: 'Choose the goods received to pay, or pay the oldest first, not both' });
    }
    if ((value.mode === 'cheque' || value.mode === 'bank_transfer') && !value.reference?.trim()) {
        context.addIssue({ code: 'custom', path: ['reference'], message: 'Enter the cheque number or the transfer reference' });
    }
    const grnIds = value.allocations.map((allocation) => allocation.grnId);
    if (new Set(grnIds).size !== grnIds.length) {
        context.addIssue({ code: 'custom', path: ['allocations'], message: 'The same goods received note appears twice' });
    }
});
export const decideSupplierPaymentSchema = z.object({
    note: z.string().trim().max(300).optional(),
});
export const reverseSupplierPaymentSchema = z.object({
    reason: z.string().trim().min(5).max(300),
});
export const voidGrnSchema = z.object({
    reason: z.string().trim().min(5).max(300),
});
export const createPurchaseReturnSchema = z.object({
    supplierId: z.string().uuid(),
    storeId: z.string().uuid(),
    grnId: z.string().uuid().nullable().optional(),
    reason: z.enum(PURCHASE_RETURN_REASON),
    creditNoteNo: z.string().trim().max(80).optional(),
    notes: noteSchema.optional(),
    lines: z
        .array(z.object({ stockBatchId: z.string().uuid(), qty: quantitySchema }))
        .min(1)
        .max(200),
});
export const closePurchaseOrderSchema = z.object({
    reason: z.string().trim().min(3).max(300),
});
export const decideStockAdjustmentSchema = z.object({
    note: z.string().trim().max(300).optional(),
});
export const createStockTakeSchema = z.object({
    storeId: z.string().uuid(),
    blind: z.boolean().default(false),
    notes: noteSchema.optional(),
    productIds: z.array(z.string().uuid()).max(500).optional(),
    therapeuticClass: z.string().trim().max(120).optional(),
});
export const recordStockTakeCountsSchema = z.object({
    entries: z
        .array(z.object({ lineId: z.string().uuid(), qty: z.number().min(0).max(1_000_000), countedAt: z.string().datetime().optional() }))
        .min(1)
        .max(500),
});
export const resolveStockTakeLineSchema = z.object({
    qty: z.number().min(0).max(1_000_000),
    note: z.string().trim().max(240).optional(),
});
export const closeStockTakeSchema = z.object({
    allowUncounted: z.boolean().default(false),
    notes: noteSchema.optional(),
});
export const setBatchStatusSchema = z
    .object({
    stockBatchIds: z.array(z.string().uuid()).min(1).max(200).optional(),
    productId: z.string().uuid().optional(),
    batchNo: z.string().trim().min(1).max(64).optional(),
    status: z.enum(BATCH_STATUS),
    reason: z.string().trim().min(3).max(300),
})
    .superRefine((value, context) => {
    const hasIds = Boolean(value.stockBatchIds?.length);
    const hasLot = Boolean(value.productId && value.batchNo);
    if (hasIds === hasLot) {
        context.addIssue({ code: 'custom', path: ['stockBatchIds'], message: 'Name either the batches, or the product and batch number' });
    }
});
export const repriceBatchesSchema = z.object({
    lines: z
        .array(z.object({ stockBatchId: z.string().uuid(), salePriceMinor: moneyMinorSchema.min(0) }))
        .min(1)
        .max(200),
    reason: z.string().trim().min(3).max(300),
});
export const supervisorOverrideSchema = z.object({
    username: z.string().trim().min(2).max(120),
    password: z.string().min(1).max(200),
});
export const payablesAgeingQuerySchema = z.object({
    asOf: isoDateSchema.optional(),
    supplierId: z.string().uuid().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(100),
    offset: z.coerce.number().int().min(0).max(100_000).default(0),
});
//# sourceMappingURL=procurement.js.map